// SubtestLabHistoryButton.js
import React, { useCallback, useState } from "react";
import axios from "axios";

import {
  Alert,
  Box,
  Button,
  Radio,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MaleIcon from "@mui/icons-material/Male";
import FemaleIcon from "@mui/icons-material/Female";

const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// ─── Shared table styles ────────────────────────────────────────────────────
const HEADER_CELL_SX = {
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  color: "#7c4dff",
};

const COLUMNS = ["Date", "Min", "Max", "Age", "NHS Number"];

// ─── Gender table ───────────────────────────────────────────────────────────
const GenderTable = ({ rows, selected, onSelect, gender }) => {
  const accentColor = gender === "Male" ? "#1565c0" : "#ad1457";
  const bgColor = gender === "Male" ? "#e3f2fd" : "#fce4ec";
  const Icon = gender === "Male" ? MaleIcon : FemaleIcon;

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {/* Gender heading */}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
        <Icon sx={{ fontSize: 18, color: accentColor }} />
        <Typography
          sx={{ fontSize: "0.8rem", fontWeight: 700, color: accentColor }}
        >
          {gender}
        </Typography>
        <Chip
          label={`${rows.length} record${rows.length !== 1 ? "s" : ""}`}
          size="small"
          sx={{
            background: bgColor,
            color: accentColor,
            fontWeight: 600,
            fontSize: "0.7rem",
            height: 20,
          }}
        />
      </Stack>

      {rows.length === 0 ? (
        <Alert severity="info" sx={{ fontSize: "0.78rem" }}>
          No {gender} records found.
        </Alert>
      ) : (
        <Box
          sx={{
            border: "1.5px solid #ede7f6",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: "#f8f9ff" }}>
                <TableCell padding="checkbox" />
                {COLUMNS.map((col) => (
                  <TableCell key={col} sx={HEADER_CELL_SX}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((row, idx) => {
                const isSelected = selected?._key === row._key;
                return (
                  <TableRow
                    key={row._key}
                    hover
                    onClick={() => onSelect(row)}
                    sx={{
                      cursor: "pointer",
                      background: isSelected ? "#f8f9ff" : "transparent",
                      "&:hover": { background: "#f3e5f5" },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Radio
                        checked={isSelected}
                        onChange={() => onSelect(row)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          color: "#7c4dff",
                          "&.Mui-checked": {
                            color: "#7c4dff",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: "0.78rem", color: "#555" }}>
                        {row.date ?? "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: "0.82rem", color: "#333" }}>
                        {row.min_val ?? "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: "0.82rem", color: "#333" }}>
                        {row.max_val ?? "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: "0.82rem", color: "#333" }}>
                        {row.age ?? "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.nhs_num ?? "—"}
                        size="small"
                        sx={{
                          background: bgColor,
                          color: accentColor,
                          fontWeight: 600,
                          fontSize: "0.72rem",
                          height: 20,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────
const SubtestLabHistoryButton = ({
  licenseKey,
  category,
  subtest,
  onApply,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [male, setMale] = useState([]); // rows already keyed
  const [female, setFemale] = useState([]);
  const [selectedMale, setSelectedMale] = useState(null);
  const [selectedFemale, setSelectedFemale] = useState(null);

  // Give every row a stable unique key so selection works across both tables

  const keyRow = (row, gender, idx) => ({
    _key: `${gender}-${idx}`,
    gender,
    min_age: null,
    max_age: null,
    min_val: row.min != null ? String(row.min) : null,
    max_val: row.max != null ? String(row.max) : null,
    // display-only fields kept for table rendering
    date: row.date,
    age: row.age,
    nhs_num: row.nhs_num,
  });

  const fetchHistory = useCallback(async () => {
    if (!licenseKey || !category || !subtest?.sub_test_name) return;

    setLoading(true);
    setError(null);

    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/subtests/lab_history/`,
        {
          license_key: licenseKey,
          category,
          subtest_name: subtest.sub_test_name,
        },
      );

      const data = res.data || {};
      const maleRows = (data.male || []).map((r, i) => keyRow(r, "Male", i));
      const femaleRows = (data.female || []).map((r, i) =>
        keyRow(r, "Female", i),
      );

      setMale(maleRows);
      setFemale(femaleRows);
      setSelectedMale(null);
      setSelectedFemale(null);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch lab history.",
      );
    } finally {
      setLoading(false);
    }
  }, [licenseKey, category, subtest]);

  const handleOpen = () => {
    setOpen(true);
    fetchHistory();
  };

  const handleClose = () => {
    setOpen(false);
    setMale([]);
    setFemale([]);
    setSelectedMale(null);
    setSelectedFemale(null);

    setError(null);
  };

  const selectRow = (row) => {
    if (row.gender === "Male") {
      setSelectedMale(row);
    } else {
      setSelectedFemale(row);
    }
  };

  const selected = [selectedMale, selectedFemale].filter(Boolean);

  const handleApply = () => {
    if (!selected.length) return;
    onApply(selected.map(({ _key, date, age, nhs_num, ...rest }) => rest));
    handleClose();
  };

  const totalRecords = male.length + female.length;

  return (
    <>
      <Tooltip title="View lab range history for this subtest">
        <span>
          <Button
            size="small"
            variant="outlined"
            disabled={disabled}
            startIcon={<HistoryIcon sx={{ fontSize: 14 }} />}
            onClick={handleOpen}
            sx={{
              fontSize: "0.6rem",
              py: 0.2,
              minWidth: 0,
              px: 1,
              mt: "-5px",
              borderColor: "#00838f",
              color: "#00838f",
              borderRadius: "6px",
              whiteSpace: "nowrap",
              "&:hover": {
                background: "#00838f",
                color: "#fff",
                borderColor: "#00838f",
              },
              "&:disabled": {
                borderColor: "#ccc",
                color: "#bbb",
              },
            }}
          />
        </span>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            border: "1.5px solid #e0f7fa",
            boxShadow: "0 8px 40px rgba(0,131,143,0.12)",
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #00838f 0%, #006064 100%)",
            color: "#fff",
            py: 1.5,
            px: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderRadius: "10px 10px 0 0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <HistoryIcon sx={{ fontSize: 18 }} />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                Lab Range History
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", opacity: 0.85 }}>
                {subtest?.sub_test_name}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={handleClose} sx={{ color: "#fff" }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2.5, pt: 2, pb: 1 }}>
          {/* Context banner */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              mb: 1.5,
              mt: 1.5,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 14, color: "#888", mt: "2px" }} />
            <Typography
              sx={{ fontSize: "0.75rem", color: "#888", lineHeight: 1.6 }}
            >
              Lab ranges received historically for this subtest, ordered by most
              recent first.
              <br />
              Category — <strong style={{ color: "#333" }}>{category}</strong>
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {/* Loading */}
          {loading && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                py: 3,
              }}
            >
              <CircularProgress size={20} sx={{ color: "#00838f" }} />
              <Typography sx={{ fontSize: "0.8rem", color: "#888" }}>
                Loading history…
              </Typography>
            </Box>
          )}

          {/* Error */}
          {!loading && !!error && (
            <Alert severity="error" sx={{ fontSize: "0.8rem" }}>
              {error}
            </Alert>
          )}

          {/* No records at all */}
          {!loading && !error && totalRecords === 0 && (
            <Alert severity="info" sx={{ fontSize: "0.8rem" }}>
              No lab history found for this subtest.
            </Alert>
          )}

          {!loading && !error && totalRecords > 0 && (
            <>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                <Chip
                  label={`${selected.length} selected`}
                  size="small"
                  sx={{
                    background: "#e0f7fa",
                    color: "#006064",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                  }}
                />
              </Stack>

              <Stack direction="row" spacing={2} alignItems="flex-start">
                <GenderTable
                  gender="Male"
                  rows={male}
                  selected={selectedMale}
                  onSelect={selectRow}
                />
                <Divider orientation="vertical" flexItem />
                <GenderTable
                  gender="Female"
                  rows={female}
                  selected={selectedFemale}
                  onSelect={selectRow}
                />
              </Stack>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2, pt: 1, gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleClose}
            sx={{
              fontSize: "0.78rem",
              borderColor: "#ccc",
              color: "#666",
              textTransform: "none",
              borderRadius: "6px",
            }}
          >
            Close
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleApply}
            disabled={!selected.length}
            startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 15 }} />}
            sx={{
              fontSize: "0.78rem",
              background: "linear-gradient(135deg, #00838f 0%, #006064 100%)",
              color: "#fff",
              textTransform: "none",
              borderRadius: "6px",
              fontWeight: 600,
              boxShadow: "0 2px 8px rgba(0,131,143,0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #006064 0%, #004d40 100%)",
              },
              "&:disabled": {
                background: "#e0e0e0",
                color: "#aaa",
                boxShadow: "none",
              },
            }}
          >
            Apply Selected
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubtestLabHistoryButton;
